import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { analyticsService } from '../../services/api/analyticsService';
import { businessService } from '../../services/api/businessService';
import {
  addDisplayCalendarDays,
  buildDailyVisitorCounts,
  countVisitorsOnDisplayDate,
  endOfDisplayDayExclusiveUtc,
  startOfDisplayDayUtc,
  toDisplayDate,
} from '../../utils/analyticsTimeZone';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import AdminLeadBreakdownModal from '../AdminLeadBreakdownModal/AdminLeadBreakdownModal';
import {
  ChartCard,
  ChartSvgWrap,
  ChartTitle,
  KpiCard,
  KpiCardButton,
  KpiCardLinkHint,
  KpiLabel,
  KpiRow,
  KpiValue,
  MetricsSection,
} from './AdminMetricsPanel.styled';

const defaultToDate = () => toDisplayDate();

const defaultFromDate = () => addDisplayCalendarDays(toDisplayDate(), -6);

function formatChartDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function getTooltipPosition(point, chartWidth, padding) {
  const tooltipWidth = 108;
  const tooltipHeight = 42;
  const gap = 12;
  let x = point.x - tooltipWidth / 2;
  let y = point.y - tooltipHeight - gap;

  const minX = padding.left;
  const maxX = chartWidth - padding.right - tooltipWidth;
  x = Math.max(minX, Math.min(maxX, x));

  if (y < padding.top) {
    y = point.y + gap;
  }

  return { x, y, width: tooltipWidth, height: tooltipHeight };
}

function VisitorLineChart({ daily }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const width = 640;
  const height = 220;
  const padding = { top: 16, right: 16, bottom: 36, left: 44 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const maxCount = Math.max(...daily.map((d) => d.count), 1);
  const points = daily.map((row, index) => {
    const x =
      padding.left +
      (daily.length <= 1 ? chartWidth / 2 : (index / (daily.length - 1)) * chartWidth);
    const y = padding.top + chartHeight - (row.count / maxCount) * chartHeight;
    return { x, y, ...row };
  });

  const hoveredPoint = hoveredIndex != null ? points[hoveredIndex] : null;
  const tooltipPos = hoveredPoint
    ? getTooltipPosition(hoveredPoint, width, padding)
    : null;

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  const areaPath =
    points.length > 0
      ? `${linePath} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`
      : '';

  const yTicks = [0, Math.ceil(maxCount / 2), maxCount];
  const labelStep = daily.length <= 7 ? 1 : Math.ceil(daily.length / 7);

  return (
    <ChartSvgWrap>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="Website visitors over time line chart"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <defs>
          <linearGradient id="visitorLineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#01ebb2" />
            <stop offset="100%" stopColor="#0d4efe" />
          </linearGradient>
          <linearGradient id="visitorAreaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="rgba(13, 78, 254, 0.18)" />
            <stop offset="100%" stopColor="rgba(13, 78, 254, 0.02)" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => {
          const y = padding.top + chartHeight - (tick / maxCount) * chartHeight;
          return (
            <g key={tick}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="rgba(13, 78, 254, 0.08)"
                strokeDasharray="4 4"
              />
              <text
                x={padding.left - 8}
                y={y + 4}
                textAnchor="end"
                fill="#8893a4"
                fontSize="11"
                fontWeight="600"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {areaPath && <path d={areaPath} fill="url(#visitorAreaGradient)" />}
        {linePath && (
          <path
            d={linePath}
            fill="none"
            stroke="url(#visitorLineGradient)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {points.map((point, index) => {
          const isHovered = hoveredIndex === index;
          return (
            <g key={point.date}>
              {isHovered && (
                <line
                  x1={point.x}
                  y1={point.y}
                  x2={point.x}
                  y2={padding.top + chartHeight}
                  stroke="rgba(13, 78, 254, 0.2)"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
              )}
              <circle
                cx={point.x}
                cy={point.y}
                r={isHovered ? 6 : 4.5}
                fill={isHovered ? '#01ebb2' : '#0d4efe'}
                stroke="#fff"
                strokeWidth="2"
                style={{ transition: 'r 0.15s ease, fill 0.15s ease' }}
              />
              <circle
                cx={point.x}
                cy={point.y}
                r="14"
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIndex(index)}
                aria-label={`${formatChartDate(point.date)}: ${point.count} visitors`}
              >
                <title>{`${formatChartDate(point.date)}: ${point.count} visitors`}</title>
              </circle>
            </g>
          );
        })}

        {points.map((point, index) =>
          index % labelStep === 0 || index === points.length - 1 ? (
            <text
              key={`${point.date}-label`}
              x={point.x}
              y={height - 10}
              textAnchor="middle"
              fill="#5a6478"
              fontSize="11"
              fontWeight="600"
            >
              {formatChartDate(point.date)}
            </text>
          ) : null,
        )}

        {hoveredPoint && tooltipPos && (
          <g pointerEvents="none">
            <rect
              x={tooltipPos.x}
              y={tooltipPos.y}
              width={tooltipPos.width}
              height={tooltipPos.height}
              rx="8"
              fill="#1a1a2e"
              stroke="rgba(1, 235, 178, 0.45)"
              strokeWidth="1"
            />
            <text
              x={tooltipPos.x + tooltipPos.width / 2}
              y={tooltipPos.y + 16}
              textAnchor="middle"
              fill="#ffffff"
              fontSize="11"
              fontWeight="600"
            >
              {formatChartDate(hoveredPoint.date)}
            </text>
            <text
              x={tooltipPos.x + tooltipPos.width / 2}
              y={tooltipPos.y + 32}
              textAnchor="middle"
              fill="#01ebb2"
              fontSize="12"
              fontWeight="700"
            >
              {hoveredPoint.count} visitor{hoveredPoint.count === 1 ? '' : 's'}
            </text>
          </g>
        )}
      </svg>
    </ChartSvgWrap>
  );
}

function AdminMetricsPanel({ onNavigateTab }) {
  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recomputeLoading, setRecomputeLoading] = useState(false);
  const [leadBreakdownOpen, setLeadBreakdownOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });

  const loadMetrics = useCallback(async () => {
    if (fromDate && toDate && fromDate > toDate) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const today = toDisplayDate();
      const chartFromTs = startOfDisplayDayUtc(fromDate).toISOString();
      const chartToTs = endOfDisplayDayExclusiveUtc(toDate).toISOString();
      const todayInRange = today >= fromDate && today <= toDate;

      const [chartData, todayData] = await Promise.all([
        analyticsService.getAppVisitorMetrics(chartFromTs, chartToTs),
        todayInRange
          ? Promise.resolve(null)
          : analyticsService.getAppVisitorMetrics(
              startOfDisplayDayUtc(today).toISOString(),
              endOfDisplayDayExclusiveUtc(today).toISOString(),
            ),
      ]);

      const sessionTimestamps = chartData.sessionTimestamps ?? [];
      const todayTimestamps = todayInRange
        ? sessionTimestamps
        : (todayData?.sessionTimestamps ?? []);

      setMetrics({
        businessCount: chartData.businessCount ?? 0,
        accountCount: chartData.accountCount ?? 0,
        businessLeadCount: chartData.businessLeadCount ?? 0,
        contactLeadCount: chartData.contactLeadCount ?? 0,
        leadBreakdown: chartData.leadBreakdown ?? null,
        todayVisitors: countVisitorsOnDisplayDate(todayTimestamps, today),
        daily: buildDailyVisitorCounts(sessionTimestamps, fromDate, toDate),
      });
    } catch (e) {
      setMetrics(null);
      setError(e.message || 'Failed to load visitor metrics');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const dateRangeInvalid = useMemo(
    () => Boolean(fromDate && toDate && fromDate > toDate),
    [fromDate, toDate],
  );

  const handleRecomputeNkyCps = async () => {
    const confirmed = window.confirm(
      'Recompute NKYCPS (NKY Connect Partner Score) for all published businesses now? This normally runs weekly.',
    );
    if (!confirmed) return;

    setRecomputeLoading(true);
    try {
      const result = await businessService.recomputeNkyCps();
      setToast({
        open: true,
        severity: 'success',
        message: `NKYCPS updated: ${result.updated} businesses (${result.errors} errors).`,
      });
    } catch (e) {
      const status = e.status;
      const message =
        status === 401 || status === 403
          ? 'Admin access required.'
          : e.message || 'Failed to recompute NKYCPS.';
      setToast({
        open: true,
        severity: 'error',
        message,
      });
    } finally {
      setRecomputeLoading(false);
    }
  };

  const datePickers = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mb: 2,
        flexWrap: 'wrap',
      }}
    >
      <CalendarTodayOutlinedIcon sx={{ fontSize: '1rem', color: '#2d46b9' }} />
      <TextField
        type="date"
        size="small"
        label="From"
        value={fromDate}
        onChange={(e) => setFromDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{
          width: 160,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontWeight: 600,
          },
        }}
      />
      <Typography sx={{ color: '#8893a4', fontWeight: 600, fontSize: '0.85rem' }}>
        to
      </Typography>
      <TextField
        type="date"
        size="small"
        label="To"
        value={toDate}
        onChange={(e) => setToDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{
          width: 160,
          '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            fontSize: '0.85rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );

  return (
    <MetricsSection>
      {loading && !metrics && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <NkySpinner />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ borderRadius: '12px', mb: 2 }}>
          {error}
        </Alert>
      )}

      {dateRangeInvalid && (
        <Alert severity="warning" sx={{ borderRadius: '12px', mb: 2 }}>
          Start date must be on or before end date.
        </Alert>
      )}

      {metrics && (
        <>
          <KpiRow>
            <KpiCard elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <PeopleOutlineIcon sx={{ color: '#0d4efe' }} />
                <KpiLabel>Today&apos;s visitors</KpiLabel>
              </Box>
              <KpiValue>{metrics.todayVisitors ?? 0}</KpiValue>
              <Typography variant="body2" sx={{ color: '#8893a4', mt: 0.5 }}>
                Unique browser sessions recorded today (Eastern Time).
              </Typography>
            </KpiCard>

            <KpiCardButton
              elevation={0}
              component="button"
              type="button"
              onClick={() => onNavigateTab?.('businesses')}
              aria-label="View business management"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <StorefrontOutlinedIcon sx={{ color: '#0d4efe' }} />
                <KpiLabel>Businesses on platform</KpiLabel>
              </Box>
              <KpiValue>{metrics.businessCount ?? 0}</KpiValue>
              <Typography variant="body2" sx={{ color: '#8893a4', mt: 0.5 }}>
                Published and unpublished provider businesses.
              </Typography>
              <KpiCardLinkHint>
                View business management
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </KpiCardLinkHint>
            </KpiCardButton>

            <KpiCardButton
              elevation={0}
              component="button"
              type="button"
              onClick={() => setLeadBreakdownOpen(true)}
              aria-label="View lead breakdown"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <ContactMailOutlinedIcon sx={{ color: '#0d4efe' }} />
                <KpiLabel>Business leads</KpiLabel>
              </Box>
              <KpiValue>{metrics.businessLeadCount ?? 0}</KpiValue>
              <Typography variant="body2" sx={{ color: '#8893a4', mt: 0.5 }}>
                All-time service inquiries, phone clicks, and email clicks.
              </Typography>
              <KpiCardLinkHint>
                View lead breakdown
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </KpiCardLinkHint>
            </KpiCardButton>

            <KpiCardButton
              elevation={0}
              component="button"
              type="button"
              onClick={() => onNavigateTab?.('users')}
              aria-label="View user management"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <ManageAccountsOutlinedIcon sx={{ color: '#0d4efe' }} />
                <KpiLabel>Accounts</KpiLabel>
              </Box>
              <KpiValue>{metrics.accountCount ?? 0}</KpiValue>
              <Typography variant="body2" sx={{ color: '#8893a4', mt: 0.5 }}>
                Registered user accounts across all roles.
              </Typography>
              <KpiCardLinkHint>
                View user management
                <ChevronRightIcon sx={{ fontSize: 18 }} />
              </KpiCardLinkHint>
            </KpiCardButton>
          </KpiRow>

          <AdminLeadBreakdownModal
            open={leadBreakdownOpen}
            onClose={() => setLeadBreakdownOpen(false)}
            leadBreakdown={metrics.leadBreakdown}
          />

          <ChartCard elevation={0}>
            <ChartTitle variant="subtitle1">
              <ShowChartOutlinedIcon sx={{ color: '#0d4efe', fontSize: '1.2rem' }} />
              Visitors over time
            </ChartTitle>
            <Typography variant="body2" sx={{ color: '#8893a4', mb: 1.5, mt: -0.5 }}>
              Stored in UTC; chart days shown in Eastern Time (EST/EDT).
            </Typography>
            {datePickers}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <NkySpinner />
              </Box>
            ) : (
              <VisitorLineChart daily={metrics.daily ?? []} />
            )}
          </ChartCard>
        </>
      )}

      <Box
        sx={{
          mt: 3,
          p: 2,
          borderRadius: '12px',
          bgcolor: '#f7fafc',
          border: '1px solid rgba(13, 78, 254, 0.1)',
        }}
      >
        <Typography sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
          NKYCPS (NKY Connect Partner Score)
        </Typography>
        <Typography variant="body2" sx={{ color: '#5a6478', mb: 1.5 }}>
          Recompute all partner scores now. Normally runs weekly on Sunday at 06:00 UTC.
        </Typography>
        <Button
          variant="contained"
          disabled={recomputeLoading}
          onClick={handleRecomputeNkyCps}
          startIcon={
            recomputeLoading ? (
              <NkySpinner size="sm" onDark />
            ) : (
              <SyncOutlinedIcon />
            )
          }
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            fontWeight: 600,
            background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
            boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
            '&:hover': {
              background: 'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
              opacity: 0.92,
            },
          }}
        >
          Recompute NKYCPS
        </Button>
      </Box>

      <NkySnackbarAlert
        open={toast.open}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        severity={toast.severity}
        message={toast.message}
      />
    </MetricsSection>
  );
}

export default AdminMetricsPanel;
